import { TMasterStandardIndustryGroup } from "@/api/entities/master-standard-industry-group.entity";
import { useGetStandardIndustryGroupList } from "@/api/services/global/master";
import { IconChevronRight } from "@/assets/icons";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { EMPTY_STR } from "@/constants/string";
import { GroupFormModal } from "@/features/master-data/components/StandardIndustry/GroupFormModal";
import { HiddenGroupModal } from "@/features/master-data/components/StandardIndustry/HiddenGroupModal";
import { StandardIndustryStyledTable } from "@/features/master-data/components/StandardIndustry/standardIndustryTable.styles";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, text } from "@/theme/colors";
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useState } from "react";
import { Link } from "react-router-dom";

type TStandardIndustryGroupListProps = {
  onSelectGroup: (group: TMasterStandardIndustryGroup) => void;
};

export const StandardIndustryGroupList: FC<TStandardIndustryGroupListProps> = ({ onSelectGroup }) => {
  const groupFormModal = useVisible();
  const createGroupModal = useVisible();
  const [editingGroup, setEditingGroup] = useState<TMasterStandardIndustryGroup>();
  const [hiddenGroup, setHiddenGroup] = useState<TMasterStandardIndustryGroup>();

  const { data: groups, isLoading } = useGetStandardIndustryGroupList({ page: 1, perPage: 100 });

  if (isLoading) return <LoadingPage />;

  return (
    <>
      <TopPage title={`「${MASTER_DATA_TYPE_TITLE.STANDARD_INDUSTRY}」の編集`} enablePageBack={false} />
      <InnerBox gap={2}>
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <Button size="sm" variant="primary" onClick={() => createGroupModal.open()}>
            大分類追加
          </Button>
        </Stack>
        <StandardIndustryStyledTable>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>大分類コード</TableCell>
                <TableCell>大分類名</TableCell>
                <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "100px", bgcolor: background.white }}>
                  有効／無効
                </TableCell>
                <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "0", bgcolor: background.white }}>編集</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell className="clickable" sx={{ minWidth: "120px", paddingLeft: "12px !important" }} onClick={() => onSelectGroup(group)}>
                    <Typography component="p" color={group.isHidden ? text.tertiary : text.primary} variant="body14">
                      {group.code || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell className="clickable" sx={{ minWidth: "320px", paddingLeft: "12px !important" }} onClick={() => onSelectGroup(group)}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography component="p" color={group.isHidden ? text.tertiary : text.primary} variant="body14">
                        {group.name || EMPTY_STR.TEXT}
                      </Typography>
                      <IconChevronRight />
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center !important", minWidth: "120px", position: "sticky", right: "100px", bgcolor: background.white }}
                  >
                    <Switch sx={{ marginRight: 0, marginLeft: 0 }} checked={!group.isHidden} onChange={() => setHiddenGroup(group)} color="primary" />
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "100px",
                      textAlign: "center !important",
                      paddingX: "4px !important",
                      position: "sticky",
                      right: "0px",
                      bgcolor: background.white,
                    }}
                  >
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingGroup(group);
                        groupFormModal.open();
                      }}
                    >
                      編集
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StandardIndustryStyledTable>
      </InnerBox>
      <FooterActionBar mt={4}>
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Button component={Link} to={AppRoutes.masterData} variant="ghost" size="sm">
            戻る
          </Button>
        </Stack>
      </FooterActionBar>

      {groupFormModal.visible && editingGroup && (
        <GroupFormModal isOpen={groupFormModal.visible} onClose={groupFormModal.close} selectedGroup={editingGroup} />
      )}
      {createGroupModal.visible && <GroupFormModal isOpen={createGroupModal.visible} onClose={createGroupModal.close} />}
      <HiddenGroupModal hiddenGroup={hiddenGroup} setHiddenGroup={setHiddenGroup} />
    </>
  );
};
